import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react"
import { useEventCallback } from "usehooks-ts"
import { create } from "zustand"
import { useOpenInspector } from "~/components/design-system/pages/internal/InspectorContext"

/**
 * Steps the highlight through the table's displayed row order. Registered by whichever table owns
 * the highlight, so the inspector's prev/next buttons and the keyboard shortcuts are one operation.
 */
export type HighlightStepper = {
  /** Moves `delta` rows through displayed (filtered + sorted) order. Returns the new id, or null. */
  step: (delta: 1 | -1) => string | null
  /** Drops the highlight. Owned by the table, like `step`, so the row repaint goes with it. */
  clear: () => void
}

/** What the hovered row's `InspectorPeekCellButton` offers the keyboard. */
export type HoveredRowActions = {
  toggle: () => void
  /** Absent when the row has no ↗. */
  openElsewhere?: () => void
}

type InspectorHighlightContextValue = {
  highlightedIds: string[]
  setHighlightedIds: (ids: string[]) => void
  /** Mirrors the current ids for ag-grid callbacks (rowClassRules), which can't wait for a re-render. */
  syncIdsRef: (ids: string[]) => void
  /** Reads that mirror: the only view a controlled page keeps current (`highlightedIds` stays
   *  empty there). */
  readIdsRef: () => string[]
  /** Calls `listener` after every write to that mirror. Returns the unsubscribe. */
  subscribeIds: (listener: () => void) => () => void
}

/**
 * A store, not context: `<Module>` renders the inspector as a sibling of the page, so a provider
 * wrapping the page never reaches it. The table publishes here; the inspector subscribes.
 */
type HighlightStepStore = {
  stepper: HighlightStepper | null
  /** Clears the highlight at its source, registered by the consumer that owns it. Independent of
   *  `stepper`: a persisted highlight outlives the grid's registration. */
  clearHighlight: (() => void) | null
  /** Where the highlighted row sits in displayed order — a published snapshot, not a grid query,
   *  so the inspector re-renders when it changes. */
  position: { index: number; total: number }
  registerStepper: (stepper: HighlightStepper) => void
  unregisterStepper: (previous: HighlightStepper) => void
  setPosition: (position: { index: number; total: number }) => void
  registerClear: (clear: () => void) => void
  unregisterClear: (previous: () => void) => void
  escapeHeld: boolean
  setEscapeHeld: (held: boolean) => void
  /** The row under the pointer. Read at key-press time with `getState()`, so it never re-renders. */
  hoveredRow: RefObject<HoveredRowActions> | null
  setHoveredRow: (row: RefObject<HoveredRowActions>) => void
  withdrawHoveredRow: (previous: RefObject<HoveredRowActions>) => void
}

const NO_POSITION = { index: -1, total: 0 }

export const useHighlightStepStore = create<HighlightStepStore>((set) => ({
  stepper: null,
  clearHighlight: null,
  position: NO_POSITION,
  registerStepper: (stepper) => set({ stepper }),
  // Identity-checked: a remount registers the new stepper before the old one unregisters, so
  // clearing unconditionally would drop a live registration.
  unregisterStepper: (previous) =>
    set((state) => (state.stepper === previous ? { stepper: null, position: NO_POSITION } : state)),
  setPosition: (position) =>
    set((state) =>
      state.position.index === position.index && state.position.total === position.total
        ? state
        : { position },
    ),
  registerClear: (clearHighlight) => set({ clearHighlight }),
  // Identity-checked, as above.
  unregisterClear: (previous) =>
    set((state) => (state.clearHighlight === previous ? { clearHighlight: null } : state)),
  escapeHeld: false,
  setEscapeHeld: (escapeHeld) => set({ escapeHeld }),
  hoveredRow: null,
  setHoveredRow: (hoveredRow) => set({ hoveredRow }),
  // Identity-checked: the next row's enter can land before this one's leave.
  withdrawHoveredRow: (previous) =>
    set((state) => (state.hoveredRow === previous ? { hoveredRow: null } : state)),
}))

const EMPTY: string[] = []

const InspectorHighlightContext = createContext<InspectorHighlightContextValue | null>(null)

export const InspectorHighlightProvider = ({ children }: { children: ReactNode }) => {
  const [highlightedIds, setHighlightedIdsState] = useState<string[]>([])
  const highlightedIdsRef = useRef<string[]>(EMPTY)
  const [listeners] = useState(() => new Set<() => void>())
  const syncIdsRef = useCallback(
    (ids: string[]) => {
      highlightedIdsRef.current = ids
      listeners.forEach((listener) => listener())
    },
    [listeners],
  )
  const readIdsRef = useCallback(() => highlightedIdsRef.current, [])
  const subscribeIds = useCallback(
    (listener: () => void) => {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
    [listeners],
  )

  const setHighlightedIds = useCallback(
    (ids: string[]) => {
      syncIdsRef(ids)
      setHighlightedIdsState(ids)
    },
    [syncIdsRef],
  )

  const value = useMemo<InspectorHighlightContextValue>(
    () => ({
      highlightedIds,
      setHighlightedIds,
      syncIdsRef,
      readIdsRef,
      subscribeIds,
    }),
    [highlightedIds, setHighlightedIds, syncIdsRef, readIdsRef, subscribeIds],
  )

  return (
    <InspectorHighlightContext.Provider value={value}>
      {children}
    </InspectorHighlightContext.Provider>
  )
}

const useInspectorHighlightInternal = () => {
  const ctx = useContext(InspectorHighlightContext)
  if (!ctx) {
    throw new Error("useInspectorHighlight must be used inside a <Module>")
  }
  return ctx
}

/**
 * Reads the current highlight, for a table repainting its row after a remount; moving it goes
 * through {@link useInspectorHighlight}. A getter: on a controlled page only the ref tracks it.
 */
export const useReadHighlightedIds = () => useInspectorHighlightInternal().readIdsRef

/** Subscribes to every write of that highlight, for a table adopting one moved from outside it. */
export const useSubscribeHighlightedIds = () => useInspectorHighlightInternal().subscribeIds

type UseInspectorHighlightOptions = {
  /** Controlled highlight (e.g. backed by a persisted store). Omit for uncontrolled. */
  value?: string[]
  /** Called whenever the highlight changes. Required in controlled mode. */
  onChange?: (ids: string[]) => void
  /** When true, highlighting (a non-empty set) also opens the inspector. */
  openOnSelect?: boolean
  /** Publishes this instance's `clear` to the inspector header. Set on the ONE consumer that owns the
   *  highlight, so the header can clear it with no table mounted. */
  publishClear?: boolean
}

/**
 * The row(s) the inspector details. Its own channel, but the table keeps it mirroring ag-grid's
 * checkbox selection: ticking rows highlights them, unticking the last clears it, and a row-side
 * highlight (click, cell focus, stepping) releases the ticks — so what is ticked is what is inspected.
 *
 * Cell focus is the single row-side input (click, arrow keys, programmatic `setFocusedCell`). A
 * control that acts on the row without asking for detail marks itself `data-no-highlight`.
 *
 * Controlled mode (`value`) requires `onChange`, or `highlight`/`clear` are silent no-ops.
 *
 * Only usable inside a `<Module>`. Approved surface: design-system/APPROVED.md.
 */
export const useInspectorHighlight = (options?: UseInspectorHighlightOptions) => {
  const ctx = useInspectorHighlightInternal()
  const open = useOpenInspector()

  const controlled = options?.value !== undefined
  const highlightedIds = controlled ? (options?.value ?? EMPTY) : ctx.highlightedIds

  // Controlled truth lives in the caller's store, so keep the grid's mirror in step with it.
  const syncIdsRef = ctx.syncIdsRef
  useEffect(() => {
    if (controlled) syncIdsRef(highlightedIds)
  }, [controlled, highlightedIds, syncIdsRef])

  const onChange = options?.onChange
  const openOnSelect = options?.openOnSelect

  const commit = useCallback(
    (next: string[]) => {
      if (controlled) {
        // Written here too, not just in the effect: ag-grid repaints the row before React commits.
        syncIdsRef(next)
        onChange?.(next)
      } else {
        ctx.setHighlightedIds(next)
      }
      if (openOnSelect && next.length > 0) open()
    },
    [controlled, onChange, ctx, syncIdsRef, openOnSelect, open],
  )

  const highlight = useCallback((id: string) => commit([id]), [commit])
  const clear = useCallback(() => commit([]), [commit])
  const setHighlightedIds = useCallback((ids: string[]) => commit(ids), [commit])
  const isHighlighted = useCallback((id: string) => highlightedIds.includes(id), [highlightedIds])

  const publishClear = options?.publishClear
  const registerClear = useHighlightStepStore((state) => state.registerClear)
  const unregisterClear = useHighlightStepStore((state) => state.unregisterClear)
  useEffect(() => {
    if (!publishClear) return
    registerClear(clear)
    return () => unregisterClear(clear)
  }, [publishClear, clear, registerClear, unregisterClear])

  return {
    highlightedIds,
    highlightedId: highlightedIds[0] ?? null,
    isHighlighted,
    highlight,
    clear,
    setHighlightedIds,
  }
}

const sameIds = (a: ReadonlySet<string>, b: readonly string[]) => {
  const unique = new Set(b)
  if (a.size !== unique.size) return false
  for (const id of unique) if (!a.has(id)) return false
  return true
}

type SelectionMirroredHighlightOptions<T> = {
  /**
   * Writes the highlight. `ids` are the keys the inspector stores, passed when the source already
   * knows them (the ticked set, including rows a search has hidden); null lets the table derive
   * them from `next`.
   */
  apply: (next: T | null, ids: readonly string[] | null) => void
  /** The keys of what is ticked right now, in the same domain the inspector stores. */
  getSelectedIds: () => readonly string[]
  /** What is ticked right now in the table's own terms — what `apply` paints. */
  getSelectedRows: () => T
  clearSelection: () => void
}

/**
 * The rule tying a table's checkbox selection to the inspector highlight, so what is ticked is what
 * is inspected. One entry point per source of a highlight change:
 *
 * - `fromSelection` — the ticks changed: highlight the ticked set; unticking the last drops the
 *   highlight, but only one the ticks still own.
 * - `fromRow` — a row-side gesture (click, peek, stepping, a clear): release the ticks, then highlight.
 * - `fromStore` — the inspector's store moved (a deeplink, a remount, the store echoing a tick):
 *   adopted as the ticks' own when it matches them, else treated like a row-side gesture.
 *
 * Ownership is held in the inspector's key domain, not the table's row ids, so a store round trip
 * that maps one key onto several rows (grouping) or fewer (a filter) never reads as a foreign write.
 * Only usable inside a `<Module>`.
 */
export const useSelectionMirroredHighlight = <T,>({
  apply,
  getSelectedIds,
  getSelectedRows,
  clearSelection,
}: SelectionMirroredHighlightOptions<T>) => {
  const readHighlightedIds = useReadHighlightedIds()
  // The keys the ticks last wrote, or null once something else moved the highlight.
  const ownedRef = useRef<ReadonlySet<string> | null>(null)

  const fromRow = useEventCallback((next: T | null) => {
    ownedRef.current = null
    // Its selection-changed echo then arrives with nothing ticked and nothing owned: a no-op.
    if (getSelectedIds().length > 0) clearSelection()
    apply(next, null)
  })

  const fromSelection = useEventCallback(() => {
    const ids = getSelectedIds()
    if (ids.length > 0) {
      ownedRef.current = new Set(ids)
      apply(getSelectedRows(), ids)
      return
    }
    const owned = ownedRef.current
    if (owned && sameIds(owned, readHighlightedIds())) {
      ownedRef.current = null
      apply(null, null)
    }
  })

  /** Returns whether the stored highlight was adopted, i.e. it was not the ticks' own. */
  const fromStore = useEventCallback((ids: readonly string[], next: T): boolean => {
    const selected = getSelectedIds()
    if (selected.length > 0 && sameIds(new Set(selected), ids)) {
      // The ticks' own highlight coming back (an echo, a remount): the ticks stay the authority, so
      // a key spanning several rows lights only the ticked ones.
      ownedRef.current = new Set(ids)
      return false
    }
    fromRow(next)
    return true
  })

  return { fromSelection, fromRow, fromStore }
}

/**
 * Registers the table's row stepper. Called by the table that owns the highlight; features don't
 * call this directly.
 */
export const useRegisterHighlightStepper = () => ({
  registerStepper: useHighlightStepStore((state) => state.registerStepper),
  unregisterStepper: useHighlightStepStore((state) => state.unregisterStepper),
  setPosition: useHighlightStepStore((state) => state.setPosition),
})

/**
 * Prev/next stepping for an inspector showing one row out of an ordered set. Reads the stepper the
 * table registered, so the inspector's buttons and the keyboard shortcuts move the same highlight.
 *
 * Returns `total: 0` when no table has registered one — step buttons have nothing to move.
 * `clearHighlight` is independent of that, and `undefined` only when nothing can drop the highlight.
 */
export const useHoldInspectorEscape = (held: boolean) => {
  const setEscapeHeld = useHighlightStepStore((state) => state.setEscapeHeld)
  useEffect(() => {
    if (!held) return
    setEscapeHeld(true)
    return () => setEscapeHeld(false)
  }, [held, setEscapeHeld])
}

export const useInspectorHighlightStep = () => {
  const stepper = useHighlightStepStore((state) => state.stepper)
  const escapeHeld = useHighlightStepStore((state) => state.escapeHeld)
  const publishedClear = useHighlightStepStore((state) => state.clearHighlight)
  const { index, total } = useHighlightStepStore((state) => state.position)

  const stepPrev = useCallback(() => stepper?.step(-1) ?? null, [stepper])
  const stepNext = useCallback(() => stepper?.step(1) ?? null, [stepper])
  // Both, idempotently: the table's clear repaints the row but is absent with no table mounted;
  // the published one reaches the highlight's store either way.
  const clearHighlight = useMemo(
    () =>
      stepper || publishedClear
        ? () => {
            stepper?.clear()
            publishedClear?.()
          }
        : undefined,
    [stepper, publishedClear],
  )

  return {
    stepPrev,
    stepNext,
    clearHighlight,
    escapeHeld,
    canStepPrev: index > 0,
    canStepNext: index >= 0 && index < total - 1,
    index,
    total,
  }
}
